import { ReactNode, useState } from "react";
import { cn } from "utils/helpers";
import { Flex } from "@chakra-ui/react";
import { FiGrid, FiList, FiPlus, FiRefreshCw } from "react-icons/fi";
import { Button } from "newui/components/Button";
import FilterButton from "components/FuncButton/FilterButton";
import FilterDrawer from "components/RFQ/CustomFilterDrawer/FilterDrawer";
import SearchBar from "components/RFQ/SearchBar/SearchBar";
import { BidderQuestionnaireHeader } from "./BidderQuestionnaireHeader";
import { SelectedModalButton } from "./SelectedModalButton";
import { useCurrentUser } from "hooks/auth";
import { RfxQuestion } from "newui/features/Rfx/types/returnable-schedule-question.type";

type SmartFieldType = "project-experience" | "key-personnel" | "corporate-documents";
export type ViewMode = "card" | "table";

interface FilterOption {
	label: string;
	value: number | string;
}

interface SmartFieldQuestionnaireBaseProps {
	[key: string]: unknown;
	addNewLabel?: string;
	answer?: {
		type?: string;
	};
	children: ReactNode;
	className?: string;
	companyId?: number;
	description?: string;
	emptyStateContent?: ReactNode;
	error?: string;
	filteringList?: FilterOption[];
	isClientPreview?: boolean;
	isLoading?: boolean;
	isRefreshing?: boolean;
	loadingMessage?: string;
	question: RfxQuestion;
	required?: boolean;
	searchPlaceholder?: string;
	selectedData?: Record<string, unknown>[];
	selectedFilter?: FilterOption[];
	showFilter?: boolean;
	showSearch?: boolean;
	smartField?: string;
	tableContent?: ReactNode;
	touched?: boolean;
	touchedField?: boolean;
	type?: SmartFieldType;
	viewMode?: ViewMode;
	onAddNew?: () => void;
	onRefresh?: () => void;
	onViewModeChange?: (mode: ViewMode) => void;
	setSearch?: (search: string) => void;
	setSelectedFilter?: (filters: FilterOption[]) => void;
}

const guestText = (
	<>
		You will be able to select from any existing items uploaded to your profile
		(example shown below).
	</>
);

export const SmartFieldQuestionnaireBase: React.FC<SmartFieldQuestionnaireBaseProps> = ({
	addNewLabel,
	className,
	question,
	required,
	smartField,
	description,
	searchPlaceholder,
	showFilter = true,
	showSearch = true,
	children,
	isLoading,
	isRefreshing,
	loadingMessage,
	filteringList,
	selectedFilter,
	setSelectedFilter,
	setSearch,
	error,
	touched,
	type,
	selectedData,
	isClientPreview,
	onAddNew,
	onRefresh,
	onViewModeChange,
	emptyStateContent,
	tableContent,
	viewMode: viewModeProp,
	...props
}) => {
	const [openDrawer, setOpenDrawer] = useState(false);
	const [viewModeInternal, setViewModeInternal] = useState<ViewMode>("card");
	const viewMode = viewModeProp ?? viewModeInternal;
	const setViewMode = (mode: ViewMode): void => {
		setViewModeInternal(mode);
		onViewModeChange?.(mode);
	};
	const { isGuest } = useCurrentUser();

	const { answer = {} } = props as { answer?: { type?: string } };
	const { type: answerType } = answer;
	const companyId = (props as { companyId?: number }).companyId;
	const disabled = (props as { disabled?: boolean }).disabled;

	const showSelectedModalButton =
		(type === "project-experience" || type === "key-personnel") &&
		selectedData?.length > 0;

	const showViewToggle =
		(type === "project-experience" || type === "key-personnel") &&
		!isClientPreview &&
		!disabled;

	return (
		<div
			className={cn(`${required && error && touched && ""} ${className} mb-2`)}
			{...props}
		>
			<Flex className="flex-col flex-wrap items-start gap-2 lg:flex-row lg:items-center lg:justify-between">
				<BidderQuestionnaireHeader
					question={question.name}
					required={required}
					error={error}
					touchedField={(props as { touchedField?: boolean })?.touchedField}
					description={description}
				/>
				<Flex className="items-center gap-2">
					{onRefresh && (
						<button
							type="button"
							onClick={onRefresh}
							disabled={isRefreshing}
							className={cn(
								"rounded-md border border-gray-300 p-2 transition-colors",
								isRefreshing
									? "cursor-not-allowed opacity-50"
									: "text-gray-500 hover:border-blue-400 hover:text-blue-500"
							)}
							aria-label="Refresh"
							data-testid="refresh-button"
						>
							<FiRefreshCw
								size={16}
								className={isRefreshing ? "animate-spin" : ""}
							/>
						</button>
					)}
					{onAddNew && addNewLabel && (
						<Button
							type="button"
							btntype="secondary"
							onClick={onAddNew}
							data-testid="add-new-button"
						>
							<FiPlus size={16} />
							{addNewLabel}
						</Button>
					)}
					{showSelectedModalButton && (
						<SelectedModalButton
							type={type as "project-experience" | "key-personnel"}
							questionId={question.id}
							companyId={companyId}
							selectedData={selectedData}
						/>
					)}
					{showViewToggle && (
						<div className="flex rounded-md border border-gray-300">
							<button
								type="button"
								onClick={() => setViewMode("card")}
								className={cn(
									"rounded-l-md p-2 transition-colors",
									viewMode === "card"
										? "bg-blue-50 text-blue-600"
										: "text-gray-400 hover:text-gray-600"
								)}
								aria-label="Card view"
								data-testid="card-view-button"
							>
								<FiGrid size={16} />
							</button>
							<button
								type="button"
								onClick={() => setViewMode("table")}
								className={cn(
									"rounded-r-md border-l border-gray-300 p-2 transition-colors",
									viewMode === "table"
										? "bg-blue-50 text-blue-600"
										: "text-gray-400 hover:text-gray-600"
								)}
								aria-label="Table view"
								data-testid="table-view-button"
							>
								<FiList size={16} />
							</button>
						</div>
					)}
					{showSearch && (
						// @ts-ignore
						<SearchBar
							setState={setSearch}
							placeholder={searchPlaceholder}
							className="min-w-[360px]"
						/>
					)}
					{showFilter && (
						<FilterButton
							onClick={() => setOpenDrawer(true)}
							filters={selectedFilter}
						/>
					)}
				</Flex>
			</Flex>
			<div>
				{isLoading && loadingMessage && (
					<div className="flex items-center gap-2 px-1 pt-4 text-sm text-gray-500">
						<div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500" />
						{loadingMessage}
					</div>
				)}
				{isClientPreview && (
					<div className="h-max min-w-[150px] rounded-md p-2 italic text-gray-500 shadow-md">
						{isGuest ? (
							guestText
						) : (
							<>
								Suppliers will be able to select from any existing items they
								have uploaded to their profile (example shown below).
								<br />
								If a supplier does not have any existing items, they can choose
								to add these and you will then have access to this
								information.{" "}
							</>
						)}
					</div>
				)}
				{emptyStateContent}
				{viewMode === "table" && tableContent ? (
					<div className="w-full py-4">
						{tableContent}
					</div>
				) : (
					<Flex className="w-full gap-5 overflow-x-auto py-6 pl-1">
						{children}
					</Flex>
				)}
				{showFilter && (
					<FilterDrawer
						isOpen={openDrawer}
						onClose={() => setOpenDrawer(false)}
						filterOptions={filteringList}
						filters={selectedFilter}
						setFilters={setSelectedFilter}
					/>
				)}
			</div>
		</div>
	);
};

// Helper function to filter data for preview mode
export const filterSmartFieldOptionForSubmittedRFQ = <
	T extends { id: number },
>(
	smartFieldData: T[],
	value: number | number[],
	isDisabled: boolean
): T[] => {
	if (isDisabled) {
		return smartFieldData?.filter((data) => {
			if (Array.isArray(value)) {
				return value?.includes(data.id);
			}

			return value === data.id;
		});
	}
	return smartFieldData;
};
